import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthAPI } from '../services/api';
import { PLANS, PlanLimits } from '../config/plans';

// Types
export interface User {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  verified: boolean;
  oauthProvider?: string;
  createdAt: string;
  lastLogin?: string;
  planId?: string;
  planLimits?: PlanLimits;
}

export interface AuthContextType {
  user: User | null;
  planLimits: PlanLimits;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  oauthLogin: (provider: string, token: string, extras?: { redirectUri?: string; codeVerifier?: string }, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  verifyEmail: (token: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

function authStorage(rememberMe: boolean): Storage {
  return rememberMe ? localStorage : sessionStorage;
}

function parsePlanLimits(profile: any): PlanLimits {
  const raw = profile?.plan_limits;
  if (!raw) return PLANS.free;
  return {
    maxAuthFileSizeMb: raw.max_auth_file_size_mb ?? PLANS.free.maxAuthFileSizeMb,
    maxAnonymousFileSizeMb: raw.max_anonymous_file_size_mb ?? PLANS.free.maxAnonymousFileSizeMb,
    maxDocumentCount: raw.max_document_count ?? PLANS.free.maxDocumentCount,
    maxSharesPerDocument: raw.max_shares_per_document ?? PLANS.free.maxSharesPerDocument,
  };
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage, then refresh from server
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check localStorage first (rememberMe=true), then sessionStorage (rememberMe=false)
        const storage = localStorage.getItem('authToken') ? localStorage : sessionStorage;
        const token = storage.getItem('authToken');
        const userData = storage.getItem('user');

        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);

          // Refresh from server to hydrate any missing fields (email for OAuth users,
          // updated plan limits, etc.) without blocking the initial render.
          try {
            const profile = await AuthAPI.getMe();
            const refreshedUser: User = {
              ...parsedUser,
              email: profile.email || parsedUser.email,
              firstName: profile.first_name ?? parsedUser.firstName,
              lastName: profile.last_name ?? parsedUser.lastName,
              verified: profile.verified ?? parsedUser.verified,
              oauthProvider: profile.oauth_provider ?? parsedUser.oauthProvider,
              planId: profile.plan_id ?? parsedUser.planId,
              planLimits: parsePlanLimits(profile),
            };
            storage.setItem('user', JSON.stringify(refreshedUser));
            setUser(refreshedUser);
          } catch {
            // Non-fatal — keep showing cached data
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe = true): Promise<void> => {
    try {
      const response = await AuthAPI.login(email, password);
      const { access_token, user_id } = response;
      const storage = authStorage(rememberMe);

      // Store token first so getMe request is authenticated
      storage.setItem('authToken', access_token);

      let firstName: string | undefined;
      let lastName: string | undefined;
      let planId = 'free';
      let planLimitsData: PlanLimits = PLANS.free;
      try {
        const profile = await AuthAPI.getMe();
        firstName = profile.first_name ?? undefined;
        lastName = profile.last_name ?? undefined;
        planId = profile.plan_id ?? 'free';
        planLimitsData = parsePlanLimits(profile);
      } catch {
        // Non-fatal — proceed with free plan defaults
      }

      const userData: User = {
        userId: user_id,
        email,
        firstName,
        lastName,
        verified: true,
        createdAt: new Date().toISOString(),
        planId,
        planLimits: planLimitsData,
      };

      storage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName?: string, lastName?: string): Promise<void> => {
    try {
      const response = await AuthAPI.register(email, password, firstName, lastName);
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const oauthLogin = async (provider: string, token: string, extras?: { redirectUri?: string; codeVerifier?: string }, rememberMe = true): Promise<void> => {
    try {
      const response = await AuthAPI.oauthLogin(provider, token, extras);
      const { access_token, user_id, email: responseEmail } = response;
      const storage = authStorage(rememberMe);

      storage.setItem('authToken', access_token);

      // Email comes directly from the OAuth response; getMe() fills in the rest.
      let email: string = responseEmail || '';
      let firstName: string | undefined;
      let lastName: string | undefined;
      let planId = 'free';
      let planLimitsData: PlanLimits = PLANS.free;
      try {
        const profile = await AuthAPI.getMe();
        if (!email) email = profile.email ?? '';
        firstName = profile.first_name ?? undefined;
        lastName = profile.last_name ?? undefined;
        planId = profile.plan_id ?? 'free';
        planLimitsData = parsePlanLimits(profile);
      } catch {
        // Non-fatal
      }

      const userData: User = {
        userId: user_id,
        email,
        firstName,
        lastName,
        verified: true,
        oauthProvider: provider,
        createdAt: new Date().toISOString(),
        planId,
        planLimits: planLimitsData,
      };

      storage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      console.error('OAuth login error:', error);
      throw error;
    }
  };

  const logout = (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    setUser(null);
  };

  const deleteAccount = async (): Promise<void> => {
    await AuthAPI.deleteAccount();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    setUser(null);
  };

  const verifyEmail = async (token: string): Promise<void> => {
    try {
      const response = await AuthAPI.verifyEmail(token);
      const { access_token, user_id } = response;

      localStorage.setItem('authToken', access_token);

      let email = '';
      let firstName: string | undefined;
      let lastName: string | undefined;
      let planId = 'free';
      let planLimitsData: PlanLimits = PLANS.free;
      try {
        const profile = await AuthAPI.getMe();
        email = profile.email ?? '';
        firstName = profile.first_name ?? undefined;
        lastName = profile.last_name ?? undefined;
        planId = profile.plan_id ?? 'free';
        planLimitsData = parsePlanLimits(profile);
      } catch {
        // Non-fatal — token is valid, proceed with free plan defaults
      }

      const userData: User = {
        userId: user_id,
        email,
        firstName,
        lastName,
        verified: true,
        createdAt: new Date().toISOString(),
        planId,
        planLimits: planLimitsData,
      };

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  };

  const planLimits: PlanLimits = user?.planLimits ?? PLANS.free;

  const value: AuthContextType = {
    user,
    planLimits,
    loading,
    login,
    register,
    oauthLogin,
    logout,
    verifyEmail,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};